import { COMPONENT_TARGET, type ComponentTarget } from './component-target.ts'
import { z } from 'zod'
import { ACTION_TYPES, isScrollActionType } from '../journey/action-types.ts'
import { CHECK_ASSERTIONS } from '../journey/check-types.ts'

export const APP_TOOLS_KEY = 'app-tools:v1'
/** Run-local action keys; never part of a stored tool step. */
export const UNSHARED_ACTION_KEYS: ReadonlySet<string> = new Set(['label', 'appTool', 'reachSurface', 'inRunAnchors'])
export const TOOL_CHECK = z.union([z.string(), z.object({
  description: z.string(), assertion: z.enum(CHECK_ASSERTIONS), component: COMPONENT_TARGET.optional(), property: z.string().optional(), selector: z.string().optional(),
  expected: z.string().optional(), label: z.string().optional(), surfaceId: z.string().optional(), controlId: z.string().optional(),
}).passthrough().refine(check => check.assertion !== 'reachSurfaceCheck' || !!check.surfaceId,
  'reachSurfaceCheck requires a surfaceId')])
type Condition = { type: 'route'; operator: 'matches' | 'not_matches'; pattern: string }
  | { type: 'selector'; operator: 'exists' | 'not_exists'; selector: string }
  | { type: 'component'; operator: 'exists' | 'not_exists'; component: ComponentTarget }
  | { type: 'all' | 'any'; conditions: Condition[] } | { type: 'not'; condition: Condition }
const CONDITION: z.ZodType<Condition> = z.lazy(() => z.union([
  z.object({ type: z.literal('route'), operator: z.enum(['matches', 'not_matches']), pattern: z.string() }),
  z.object({ type: z.literal('component'), operator: z.enum(['exists', 'not_exists']), component: COMPONENT_TARGET }),
  z.object({ type: z.literal('selector'), operator: z.enum(['exists', 'not_exists']), selector: z.string() }),
  z.object({ type: z.enum(['all', 'any']), conditions: z.array(CONDITION) }),
  z.object({ type: z.literal('not'), condition: CONDITION }),
]))
const CURRENT_TOOL_ACTION = z.object({
  type: z.enum(ACTION_TYPES).exclude(['callTool']), action: z.string().min(1), checks: z.array(TOOL_CHECK),
  selector: z.string().optional(), value: z.string().optional(), variable: z.string().optional(),
  append: z.boolean().optional().describe('For type/input only: append at the end without selecting or replacing existing content; default false replaces the field.'),
  recipient: z.string().optional(), attribute: z.string().optional(), context: z.string().optional(),
  surfaceId: z.string().optional(), controlId: z.string().optional(), label: z.string().optional(),
  controlName: z.string().optional(), component: COMPONENT_TARGET.optional(), destination: COMPONENT_TARGET.optional(),
  condition: CONDITION.optional(), requiresAuth: z.boolean().optional(),
  // Run-local (UNSHARED_ACTION_KEYS): a missed strip fails the write instead of persisting.
  inRunAnchors: z.never().optional(),
}).passthrough().refine(a => !a.append || a.type === 'type' || a.type === 'input', 'append is only valid for type/input').refine(a => !(a.type === 'reachSurface' && a.surfaceId), 'Tools contain steps, not nested tool/surface references')
// A nested reference is a step that still has to be expanded inside the tool: a `callTool`, or a
// `reachSurface` carrying the surfaceId it names. A `reachSurface` WITHOUT a surfaceId is not a
// reference at all — it is an inert goal step the runner executes as a no-op while its checks drive
// recovery, so it may live in a tool's steps like any other step.
// Read old catalog/session data without retaining an executable session-reset command.
// The converted step also carries WHY it requires auth — an explicit stored restore-saved, not an
// authored marker — as the `sessionReset: 'restore-saved'` provenance field. The runner's saved-session
// gate reads it (journey/auth-session.ts revisionDemandsSavedSession): a reach revision that stored an
// explicit session restore is the journey's own declaration that it does NOT drive its own sign-in, so
// the gate must demand the saved session even when a later step references a $login_* token as DATA
// (the new account email typed into an authenticated Settings surface) — without the marker the gate
// cannot tell that shape from a journey that signs itself in, and it boots the run in the public root's
// clean state to end at a registration wall before the surface the journey covers is reached
// (Bug Report #14683, eval-mtln0ga1-66jbik3o).
const LEGACY_SESSION_STEP = z.object({
  type: z.literal('resetSession'), value: z.enum(['clean', 'preserve', 'restore-saved']),
  action: z.string(), checks: z.array(TOOL_CHECK),
}).passthrough().transform(({ value, ...step }) => ({
  // `value` is DELETED, not set to undefined: zod keeps a key set to undefined as an own property of
  // the parse result, so `value: undefined` made a freshly converted legacy step deep-UNEQUAL to the
  // stored checkpoint form it normalizes into (JSON.stringify drops the key on both sides, so the
  // two read identical everywhere except `isDeepStrictEqual`, which counts own keys) — and the
  // conversion's own catalog identity match (storeToolGroups' `r.steps` deep-equal) can never fire,
  // so a legacy reach group was always re-authored as `currentRevision + 1` carrying the read
  // transform's fabricated arrival check instead of binding the stored binding revision (Bug Report
  // #19321, eval-mtaiz0fl: the envelope's corrected `/login` bar at currentRevision 2 was displaced
  // by an appended revision 3 whose generic reachSurfaceCheck no live page could satisfy).
  ...step, type: 'checkpoint' as const,
  action: 'Verify the starting page', requiresAuth: value === 'restore-saved',
  ...(value === 'restore-saved' ? { sessionReset: 'restore-saved' as const } : {}),
}))
export const TOOL_ACTION = z.union([CURRENT_TOOL_ACTION, LEGACY_SESSION_STEP]).pipe(CURRENT_TOOL_ACTION)

/**
 * A stored legacy `resetSession` step in its checkpoint form, for stored steps that bypass TOOL_ACTION.
 * @deprecated no longer authored since 1572f9b504 (reach plans emit a checkpoint); eval seeding still copies pre-cutoff journeys verbatim 2026-09-19
 */
export function normalizeLegacySessionStep<T extends { type: string }>(step: T): T {
  const legacy = LEGACY_SESSION_STEP.safeParse(step)
  return legacy.success ? legacy.data as typeof legacy.data & T : step
}
export const TOOL_STEPS = z.object({
  actions: z.array(TOOL_ACTION).min(1),
  variables: z.array(z.object({ name: z.string().regex(/^[a-zA-Z_]\w*$/), init: z.string().optional() })).default([]),
  contexts: z.array(z.string()).default([]),
})
export const TOOL_REVISION = z.object({
  revision: z.number().int().positive(), steps: TOOL_STEPS,
  startingSurfaceId: z.string().min(1).optional(),
  projectVariables: z.array(z.string().regex(/^[a-zA-Z_]\w*$/)).optional(),
  // Compatibility marker: learned replacements must not acquire another arrival check on read.
  reachCheckVersion: z.literal(1).optional(),
  // Compatibility marker: compiles carry the judged viewport classification; legacy scroll compiles re-derived on read.
  viewportRequirementVersion: z.literal(1).optional(),
  source: z.enum(['generated', 'surface', 'recovery', 'abstract']),
  sourceRunId: z.string().optional(), createdAt: z.string(),
  // The idempotency key of the logical write that added this revision (store.ts commitToolWrite).
  writeId: z.string().optional(),
})
type ToolAction = z.infer<typeof TOOL_ACTION>
type ToolCheck = z.infer<typeof TOOL_CHECK>
const isAbstract = (check: ToolCheck) => typeof check !== 'string' && check.assertion === 'abstract'
// A revision without `viewportRequirementVersion` predates the judge classifying viewport requirements
// (decision-rules.ts requireViewportEvidence): its compiles of abstract checks may be weaker than authored.
// Scroll actions select which legacy compiles re-derive: their authored requirement is the likeliest viewport
// one, and re-deriving costs one judgement. The authored checks come from the tool's own history: the action
// whose [action, checks] its `repairObjective` serializes (call-level checks follow them in the objective).
function authoredScrollChecks(action: ToolAction, history: readonly ToolRevision[]): ToolCheck[] | null {
  if (!isScrollActionType(action.type) || action.repairPrerequisite || typeof action.repairObjective !== 'string') return null
  if (action.checks.some(check => isAbstract(check) || typeof check !== 'string' && (check.assertion === 'in_view' || check.assertion === 'reachSurfaceCheck'))) return null
  let objective: unknown
  try { objective = JSON.parse(action.repairObjective) } catch { return null }
  if (!Array.isArray(objective) || objective.length !== 2) return null
  const [intent, authored] = objective as unknown[]
  const checks = z.array(TOOL_CHECK).safeParse(authored)
  if (!checks.success) return null
  const serialized = checks.data.map(check => JSON.stringify(check))
  const source = history.flatMap(revision => revision.steps.actions).find(candidate => candidate.action === intent
    && candidate.checks.some(isAbstract) && candidate.checks.every((check, k) => JSON.stringify(check) === serialized[k]))
  return source ? source.checks : null
}
function withAuthoredScrollChecks(revision: ToolRevision, history: readonly ToolRevision[]): ToolRevision {
  if (revision.viewportRequirementVersion === 1) return revision
  const actions = revision.steps.actions.map(action => {
    const checks = authoredScrollChecks(action, history)
    return checks ? { ...action, checks } : action
  })
  return { ...revision, viewportRequirementVersion: 1 as const, steps: { ...revision.steps, actions } }
}
const TOOL_DEFINITION = z.object({
  id: z.string().min(1), name: z.string().regex(/^[a-z][a-z0-9_]*$/), description: z.string().min(1),
  displayName: z.string().min(1).optional(),
  requiresAuth: z.boolean().optional(),
  parameters: z.array(z.string().regex(/^[a-zA-Z_]\w*$/)).default([]),
  reachSurfaceId: z.string().optional(),
  operatingSurfaceId: z.string().min(1).optional(),
  surfaceIds: z.array(z.string()).default([]), currentRevision: z.number().int().positive(),
  revisions: z.array(TOOL_REVISION).min(1),
})
export const APP_TOOL = TOOL_DEFINITION.superRefine((tool, ctx) => {
  if (!tool.revisions.some(r => r.revision === tool.currentRevision)) ctx.addIssue({ code: 'custom', message: 'Current tool revision is missing' })
  if (new Set(tool.revisions.map(r => r.revision)).size !== tool.revisions.length) ctx.addIssue({ code: 'custom', message: 'Duplicate tool revision' })
 }).transform(tool => ({
  ...tool,
  revisions: tool.revisions.map(revision => withAuthoredScrollChecks(revision, tool.revisions)).map(revision => {
    if (!tool.reachSurfaceId || revision.reachCheckVersion === 1) return revision
    const actions = structuredClone(revision.steps.actions)
    const last = actions.at(-1)!
    if (!actions.some(action => action.checks.some(check => typeof check !== 'string'
      && check.assertion === 'reachSurfaceCheck' && check.surfaceId === tool.reachSurfaceId))) {
      last.checks.push({ assertion: 'reachSurfaceCheck', surfaceId: tool.reachSurfaceId,
        description: `The destination is reached: ${tool.displayName ?? tool.description} (${tool.reachSurfaceId})` })
    }
    return { ...revision, reachCheckVersion: 1 as const, steps: { ...revision.steps, actions } }
  }),
  requiresAuth: tool.requiresAuth ?? tool.revisions.some(revision => revision.steps.actions.some(step => step.requiresAuth === true)),
}))
// New proposals use current actions; compatibility transforms belong only to stored-data reads.
export const AUTHORED_TOOL_REVISION = TOOL_REVISION.extend({
  steps: TOOL_STEPS.extend({ actions: z.array(CURRENT_TOOL_ACTION).min(1) }),
})
export const AUTHORED_APP_TOOL = TOOL_DEFINITION.extend({
  revisions: z.array(AUTHORED_TOOL_REVISION).min(1),
}).transform((tool, ctx) => {
  const parsed = APP_TOOL.safeParse(tool)
  if (parsed.success) return parsed.data
  parsed.error.issues.forEach(issue => ctx.addIssue(issue))
  return z.NEVER
})
export const TOOL_CATALOG = z.object({ version: z.literal(1), tools: z.array(APP_TOOL) }).superRefine((catalog, ctx) => {
  if (new Set(catalog.tools.map(t => t.id)).size !== catalog.tools.length) ctx.addIssue({ code: 'custom', message: 'Duplicate tool ID' })
  if (new Set(catalog.tools.map(t => t.name)).size !== catalog.tools.length) ctx.addIssue({ code: 'custom', message: 'Duplicate tool name' })
})
export const TOOL_CALL = z.object({
  type: z.literal('callTool'), toolId: z.string().min(1), action: z.string(), checks: z.array(TOOL_CHECK).default([]),
  toolRevision: z.number().int().positive().optional(), arguments: z.record(z.string()).default({}),
  context: z.string().optional(),
  surfaceId: z.string().min(1).optional(),
}).strict()
export type AppTool = z.infer<typeof APP_TOOL>
export type ToolCatalog = z.infer<typeof TOOL_CATALOG>
export type ToolSteps = z.infer<typeof TOOL_STEPS>
export type ToolCall = z.infer<typeof TOOL_CALL>
export type ToolRevision = z.infer<typeof TOOL_REVISION>
